import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Share, Heart, Star, Users, Bed, Bath, Wifi, Car, Utensils, Tv, Wind, Dumbbell, MapPin, Phone, Waves as Pool, Flame, Shirt, Snowflake, Music, Zap, Shield, Coffee, UtensilsCrossed, Trees } from 'lucide-react';
import LoginModal from '../components/LoginModal';
import RegisterModal from '../components/RegisterModal';
import PaymentModal from '../components/PaymentModal';
import PropertyMap from '../components/PropertyMap';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { calculatePrice } from '../api/pricing';
import { getPropertyAvailability } from '../api/properties';

const AMENITY_ICONS = {
  'Wi-Fi': Wifi, 'Free Parking': Car, 'Kitchen': Utensils, 'Full kitchen': Utensils,
  'TV': Tv, 'Smart TV': Tv, 'Air Conditioning': Wind, 'Heating': Flame, 'Gym': Dumbbell,
  'Pool': Pool, 'Hot Tub': Bath, 'Washer': Shirt, 'Dryer': Shirt, 'Coffee Maker': Coffee,
  'Dishwasher': UtensilsCrossed, 'BBQ Grill': Flame, 'Garden': Trees, 'Workspace': Zap,
  'Sound System': Music, 'Beach Access': Pool, 'Smoke Detector': Shield,
  'First Aid Kit': Shield, 'Safe': Shield, 'EV Charging Station': Zap,
};

const amenityToIcon = (name) => AMENITY_ICONS[name] || Zap;

const PropertyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, isLoading: authLoading } = useAuth();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [showGallery, setShowGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [showAllReviewsModal, setShowAllReviewsModal] = useState(false);
  const [showContactHostModal, setShowContactHostModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Default dates: today and 5 days from now
  const today = new Date().toISOString().split('T')[0];
  const fiveDaysFromNow = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [selectedDates, setSelectedDates] = useState({
    checkIn: today,
    checkOut: fiveDaysFromNow
  });
  const [selectedGuests, setSelectedGuests] = useState(2);
  const [pricingBreakdown, setPricingBreakdown] = useState(null);

  // Calendar state
  const [bookedRanges, setBookedRanges] = useState([]);
  const [showCheckInCal, setShowCheckInCal] = useState(false);
  const [showCheckOutCal, setShowCheckOutCal] = useState(false);
  const [calInMonth, setCalInMonth] = useState(() => new Date(today + 'T00:00:00'));
  const [calOutMonth, setCalOutMonth] = useState(() => new Date(today + 'T00:00:00'));
  const calendarRef = useRef(null);

  const openGallery = (index) => {
    setCurrentImageIndex(index);
    setShowGallery(true);
  };

  const closeGallery = () => {
    setShowGallery(false);
  };

  const navigateGallery = (direction) => {
    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
    } else {
      setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };

  // Swipe handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      navigateGallery('next');
    }
    if (isRightSwipe) {
      navigateGallery('prev');
    }
  };

  const handleReserveNow = async () => {
    const { checkIn, checkOut } = selectedDates;
    const guests = selectedGuests;

    let pricing = pricingBreakdown;
    if (!pricing) {
      const nights = Math.max(1, Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)));
      pricing = { nights, basePrice: property.price, nightlyRate: property.price, subtotal: property.price * nights };
    }

    const nights = pricing.nights;
    const nightlyTotal = pricing.subtotal;
    const securityDeposit = property.securityDeposit || 0;
    const taxes = Math.round(nightlyTotal * 0.075);
    const total = nightlyTotal + securityDeposit + taxes;

    const bookingDetails = {
      checkIn,
      checkOut,
      nights,
      guests: `${guests} guests`,
      nightlyRate: pricing.nightlyRate,
      nightlyTotal,
      pricingBreakdown: pricing,
      securityDeposit,
      taxes,
      total,
    };

    setBookingData(bookingDetails);

    if (isLoggedIn) {
      setShowPaymentModal(true);
    } else {
      setShowLoginModal(true);
    }
  };

  const handleLoginSuccess = (userData) => {
    // After successful login, show payment modal
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = (confirmedBooking) => {
    navigate(`/booking-confirmed/${confirmedBooking.id || id}`);
  };

  const closeLoginModal = () => {
    setShowLoginModal(false);
  };

  const openRegisterModal = () => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
  };

  const closeRegisterModal = () => {
    setShowRegisterModal(false);
  };

  const handleRegisterSuccess = (userData) => {
    // After successful registration, show payment modal
    setShowRegisterModal(false);
    setShowPaymentModal(true);
  };

  const handleSwitchToLogin = () => {
    setShowRegisterModal(false);
    setShowLoginModal(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
  };

  const handleShowAllAmenities = () => {
    setShowAllAmenities(!showAllAmenities);
  };

  const handleShowAllReviews = () => {
    setShowAllReviewsModal(true);
  };

  const closeAllReviewsModal = () => {
    setShowAllReviewsModal(false);
  };

  const handleContactHost = () => {
    setShowContactHostModal(true);
  };

  const closeContactHostModal = () => {
    setShowContactHostModal(false);
  };

  const handleSendMessage = (message) => {
    // In a real app, this would send the message to the host
    alert(`Message sent to Roar Homes: ${message}`);
    setShowContactHostModal(false);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy link');
    }
  };

  const shareViaNative = async () => {
    const shareData = {
      title: property.title,
      text: `Check out this amazing property: ${property.title} in ${property.location}`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        copyToClipboard(window.location.href);
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const { getPropertyById } = require('../api/properties');
        const data = await getPropertyById(id);
        if (data) {
          setProperty({
            id: data.id,
            securityDeposit: parseFloat(data.securityDeposit) || 0,
            title: data.name,
            rating: (data.Reviews || data.reviews || []).length > 0
              ? Number(((data.Reviews || data.reviews).reduce((acc, r) => acc + r.rating, 0) / (data.Reviews || data.reviews).length).toFixed(1))
              : 0,
            reviewCount: (data.Reviews || data.reviews || []).length,
            location: data.city ? `${data.city}, ${data.country || ''}` : data.address,
            coordinates: null,
            guests: data.maxGuests || 8,
            bedrooms: data.bedrooms || 4,
            bathrooms: data.bathrooms || 3,
            price: data.pricePerNight || 450,
            description: data.description || "Beautiful property",
            description2: "Enjoy your stay at this wonderful place.",
            host: {
              name: "Roar Homes",
              image: "", // Hidden for now
              status: "Certified Host",
              experience: "Premium Short-term Rentals"
            },
            images: data.images?.length > 0 ? data.images : [
              "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&h=600&fit=crop",
              "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=400&h=300&fit=crop",
              "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&h=300&fit=crop",
              "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&h=300&fit=crop",
              "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400&h=300&fit=crop"
            ],
            amenities: (Array.isArray(data.amenities) ? data.amenities : []).map(name => ({
              icon: amenityToIcon(name),
              name,
            })),
            reviews: (data.Reviews || data.reviews || []).map(r => ({
              name: r.User ? `${r.User.firstName} ${r.User.lastName}` : "Guest",
              image: r.User?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(r.User?.firstName || 'G')}&background=random`,
              date: new Date(r.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
              comment: r.content
            }))
          });
        }
      } catch (error) {
        console.error('Failed to fetch property details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  // Fetch booked date ranges for this property
  useEffect(() => {
    if (!property) return;
    getPropertyAvailability(property.id)
      .then(setBookedRanges)
      .catch(() => setBookedRanges([]));
  }, [property]);

  // Close calendars on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCheckInCal(false);
        setShowCheckOutCal(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toDateTs = (d) => {
    // Strip time component if present (API returns full ISO strings from DataTypes.DATE)
    const dateOnly = typeof d === 'string' && d.includes('T') ? d.split('T')[0] : d;
    return new Date(dateOnly + 'T00:00:00').getTime();
  };

  const isDateBooked = (dateStr) => {
    const ts = toDateTs(dateStr);
    return bookedRanges.some(({ checkInDate, checkOutDate }) => {
      const start = toDateTs(checkInDate);
      const end = toDateTs(checkOutDate);
      return ts >= start && ts < end;
    });
  };

  const generateCalDays = (type) => {
    const currentDate = type === 'checkin' ? calInMonth : calOutMonth;
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const todayTs = new Date(today + 'T00:00:00').getTime();
    const ciTs = selectedDates.checkIn ? new Date(selectedDates.checkIn + 'T00:00:00').getTime() : null;
    const coTs = selectedDates.checkOut ? new Date(selectedDates.checkOut + 'T00:00:00').getTime() : null;
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`e-${i}`} />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const ts = new Date(dateStr + 'T00:00:00').getTime();
      const isPast = ts < todayTs;
      const isBooked = isDateBooked(dateStr);
      const isInvalidOut = type === 'checkout' && ciTs && ts <= ciTs;
      const isUnavailable = isPast || isBooked || isInvalidOut;
      const isSelected = (type === 'checkin' ? selectedDates.checkIn : selectedDates.checkOut) === dateStr;
      const isInRange = !isSelected && ciTs && coTs && ts > ciTs && ts < coTs;

      days.push(
        <div
          key={day}
          onClick={() => {
            if (isUnavailable) return;
            if (type === 'checkin') {
              setSelectedDates(prev => {
                const next = { ...prev, checkIn: dateStr };
                if (prev.checkOut && new Date(prev.checkOut + 'T00:00:00').getTime() <= ts) {
                  next.checkOut = '';
                }
                return next;
              });
              setShowCheckInCal(false);
              setShowCheckOutCal(true);
            } else {
              setSelectedDates(prev => ({ ...prev, checkOut: dateStr }));
              setShowCheckOutCal(false);
            }
          }}
          className={`text-center text-sm py-1.5 rounded-md select-none transition-colors ${isBooked
              ? 'text-red-300 line-through cursor-not-allowed'
              : isPast || isInvalidOut
                ? 'text-gray-300 cursor-not-allowed'
                : isSelected
                  ? 'bg-pink-500 text-white font-semibold cursor-pointer'
                  : isInRange
                    ? 'bg-pink-100 text-pink-700 cursor-pointer'
                    : 'text-gray-700 hover:bg-gray-100 cursor-pointer'
            }`}
        >
          {day}
        </div>
      );
    }
    return days;
  };

  // Recalculate pricing breakdown whenever dates or loaded property changes
  useEffect(() => {
    if (!property) return;
    let cancelled = false;
    const fetch = async () => {
      try {
        const pricing = await calculatePrice(property.id, selectedDates.checkIn, selectedDates.checkOut);
        if (!cancelled) setPricingBreakdown(pricing);
      } catch {
        if (!cancelled) setPricingBreakdown(null);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [property, selectedDates.checkIn, selectedDates.checkOut]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-pink-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-600">Loading details...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">Property not found.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/properties" className="p-2 rounded-lg hover:bg-gray-100">
                <ArrowLeft className="text-gray-600" size={20} />
              </Link>
              <span className="ml-3 text-lg font-semibold text-gray-900">Property Details</span>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleShare}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Share className="text-gray-600" size={20} />
              </button>
              <button
                onClick={() => toggleFavorite(property)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Heart
                  className={isFavorite(property.id) ? "text-red-500 fill-red-500" : "text-gray-600"}
                  size={20}
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Photo Gallery */}
        <div className="mb-8">
          {/* Desktop Layout */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-2">
            <div className="lg:col-span-2">
              <img
                src={property.images[0]}
                alt="Main property view"
                className="w-full h-96 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => openGallery(0)}
              />
            </div>
            <div className="lg:col-span-2 grid grid-cols-2 gap-2">
              {property.images.slice(1, 5).map((image, index) => {
                const isLast = index === Math.min(3, property.images.length - 2);
                const hasMore = property.images.length > 5 && isLast;
                return (
                  <div key={index} className="relative">
                    <img
                      src={image}
                      alt={`Property view ${index + 2}`}
                      className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => openGallery(index + 1)}
                    />
                    {hasMore && (
                      <div
                        className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center cursor-pointer hover:bg-opacity-60 transition-all"
                        onClick={() => openGallery(0)}
                      >
                        <span className="text-white font-semibold">+{property.images.length - 5} more photos</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="lg:hidden">
            <div className="mb-2">
              <img
                src={property.images[0]}
                alt="Main property view"
                className="w-full h-48 sm:h-64 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => openGallery(0)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <img
                src={property.images[1]}
                alt="Property view 2"
                className="w-full h-24 sm:h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => openGallery(1)}
              />
              <div className="relative">
                <img
                  src={property.images[2]}
                  alt="More photos"
                  className="w-full h-24 sm:h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => openGallery(2)}
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center cursor-pointer hover:bg-opacity-60 transition-all" onClick={() => openGallery(0)}>
                  <span className="text-white font-semibold text-xs sm:text-sm">+{property.images.length - 3} more photos</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Property Info */}
          <div className="lg:col-span-2">
            {/* Title and Rating */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{property.title}</h1>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <Star className="text-yellow-400 mr-1" size={16} />
                  <span className="font-semibold">{property.rating}</span>
                  <span className="ml-1">({property.reviewCount} reviews)</span>
                </div>
                <span>•</span>
                <span>{property.location}</span>
              </div>
            </div>

            {/* Quick Info */}
            <div className="flex items-center space-x-6 mb-6 text-gray-600">
              <div className="flex items-center">
                <Users className="mr-2" size={20} />
                <span>{property.guests} guests</span>
              </div>
              <div className="flex items-center">
                <Bed className="mr-2" size={20} />
                <span>{property.bedrooms} bedrooms</span>
              </div>
              <div className="flex items-center">
                <Bath className="mr-2" size={20} />
                <span>{property.bathrooms} bathrooms</span>
              </div>
            </div>


            {/* Description */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">About this place</h2>
              <p className="text-gray-700 leading-relaxed mb-4">{property.description}</p>
              <p className="text-gray-700 leading-relaxed">{property.description2}</p>
            </div>

            {/* Amenities */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">What this place offers</h2>
              <div className="grid grid-cols-2 gap-4">
                {(showAllAmenities ? property.amenities : property.amenities.slice(0, 8)).map((amenity, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <amenity.icon className="text-blue-500" size={20} />
                    <span className="text-gray-700">{amenity.name}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={handleShowAllAmenities}
                className="mt-4 text-blue-600 font-semibold hover:underline"
              >
                {showAllAmenities ? `Show less` : `Show all ${property.amenities.length} amenities`}
              </button>
            </div>

            {/* Reviews */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Reviews</h2>
                <div className="flex items-center">
                  <Star className="text-yellow-400 mr-1" size={16} />
                  <span className="font-semibold">
                    {property.rating > 0 ? `${property.rating} • ${property.reviewCount} reviews` : 'New'}
                  </span>
                </div>
              </div>

              {property.reviews.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {property.reviews.slice(0, 2).map((review, index) => (
                      <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-3 mb-3">
                          <img src={review.image} alt={review.name} className="w-10 h-10 rounded-full" />
                          <div>
                            <p className="font-semibold text-gray-900">{review.name}</p>
                            <p className="text-sm text-gray-600">{review.date}</p>
                          </div>
                        </div>
                        <p className="text-gray-700 text-sm">"{review.comment}"</p>
                      </div>
                    ))}
                  </div>

                  {property.reviews.length > 2 && (
                    <button
                      onClick={handleShowAllReviews}
                      className="w-full py-3 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50"
                    >
                      Show all {property.reviews.length} reviews
                    </button>
                  )}
                </>
              ) : (
                <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
                  <p className="text-gray-500 italic">No reviews yet for this property.</p>
                </div>
              )}
            </div>
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 sticky top-24">
              <div className="mb-6">
                <div className="flex items-baseline space-x-2">
                  <span className="text-3xl font-bold text-gray-900">₦{property.price?.toLocaleString()}</span>
                  <span className="text-gray-600">per night</span>
                </div>
                <div className="flex items-center mt-2">
                  <Star className="text-yellow-400 mr-1" size={16} />
                  <span className="font-semibold">
                    {property.rating > 0 ? property.rating : 'New'}
                  </span>
                  {property.rating > 0 && (
                    <span className="text-gray-600 ml-1">({property.reviewCount} reviews)</span>
                  )}
                </div>
              </div>

              {/* Date Selection */}
              <div className="mb-4 relative" ref={calendarRef}>
                <div className="grid grid-cols-2 gap-0 border border-gray-300 rounded-lg overflow-visible">
                  <div
                    className={`p-3 border-r border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors rounded-l-lg ${showCheckInCal ? 'bg-pink-50 border-pink-300' : ''}`}
                    onClick={() => { setShowCheckInCal(v => !v); setShowCheckOutCal(false); }}
                  >
                    <label className="block text-xs font-semibold text-gray-700 mb-1 cursor-pointer">CHECK-IN</label>
                    <p className="text-sm text-gray-900 font-medium">
                      {selectedDates.checkIn
                        ? new Date(selectedDates.checkIn + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : 'Select date'}
                    </p>
                  </div>
                  <div
                    className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors rounded-r-lg ${showCheckOutCal ? 'bg-pink-50 border-pink-300' : ''}`}
                    onClick={() => { setShowCheckOutCal(v => !v); setShowCheckInCal(false); }}
                  >
                    <label className="block text-xs font-semibold text-gray-700 mb-1 cursor-pointer">CHECK-OUT</label>
                    <p className="text-sm text-gray-900 font-medium">
                      {selectedDates.checkOut
                        ? new Date(selectedDates.checkOut + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : 'Select date'}
                    </p>
                  </div>
                </div>

                {/* Calendar dropdown — shared panel */}
                {(showCheckInCal || showCheckOutCal) && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <button
                        onClick={() => {
                          if (showCheckInCal) { const m = new Date(calInMonth); m.setMonth(m.getMonth() - 1); setCalInMonth(m); }
                          else { const m = new Date(calOutMonth); m.setMonth(m.getMonth() - 1); setCalOutMonth(m); }
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-600 font-bold transition-colors"
                      >‹</button>
                      <span className="text-sm font-semibold text-gray-800">
                        {(showCheckInCal ? calInMonth : calOutMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <button
                        onClick={() => {
                          if (showCheckInCal) { const m = new Date(calInMonth); m.setMonth(m.getMonth() + 1); setCalInMonth(m); }
                          else { const m = new Date(calOutMonth); m.setMonth(m.getMonth() + 1); setCalOutMonth(m); }
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-600 font-bold transition-colors"
                      >›</button>
                    </div>

                    <div className="grid grid-cols-7 text-center text-xs font-semibold text-gray-400 mb-2">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i}>{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5">
                      {generateCalDays(showCheckInCal ? 'checkin' : 'checkout')}
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-pink-500 inline-block"></span>Selected
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-pink-100 border border-pink-200 inline-block"></span>Your stay
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-red-200 inline-block"></span>Unavailable
                      </span>
                    </div>

                    <p className="text-xs text-gray-400 mt-2 text-center">
                      {showCheckInCal ? 'Select your check-in date' : 'Select your check-out date'}
                    </p>
                  </div>
                )}
              </div>

              {/* Guests */}
              <div className="mb-6">
                <div className="border border-gray-300 rounded-lg p-3">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">GUESTS</label>
                  <select
                    className="w-full text-sm text-gray-900 bg-transparent border-none focus:outline-none"
                    value={selectedGuests}
                    onChange={(e) => setSelectedGuests(parseInt(e.target.value))}
                  >
                    {[...Array(property.guests - 1)].map((_, i) => (
                      <option key={i + 2} value={i + 2}>{i + 2} guests</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Reserve Button */}
              <button
                onClick={handleReserveNow}
                disabled={authLoading}
                className="w-full bg-gradient-to-r from-pink-500 to-red-500 text-white font-semibold py-3 rounded-lg hover:from-pink-600 hover:to-red-600 transition-all duration-200 mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {authLoading ? 'Loading...' : 'Reserve Now'}
              </button>

              {/* <p className="text-center text-sm text-gray-600 mb-4">You won't be charged yet</p> */}

              {/* Price Breakdown */}
              {(() => {
                const pb = pricingBreakdown;
                const nights = pb?.nights ?? Math.max(1, Math.ceil((new Date(selectedDates.checkOut) - new Date(selectedDates.checkIn)) / (1000 * 60 * 60 * 24)));
                const nightlyTotal = pb?.subtotal ?? (property.price * nights);
                const securityDeposit = property.securityDeposit || 0;
                const taxes = Math.round(nightlyTotal * 0.075);
                const total = nightlyTotal + securityDeposit + taxes;

                const isSplit = pb && pb.ruleNights > 0 && pb.baseNights > 0;

                return (
                  <div className="space-y-3 mb-4">
                    {isSplit ? (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">₦{pb.basePrice?.toLocaleString()} × {pb.baseNights} night{pb.baseNights !== 1 ? 's' : ''}</span>
                          <span className="text-gray-900">₦{(pb.basePrice * pb.baseNights).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-pink-600 font-medium">{pb.appliedRule.label}: ₦{pb.appliedRule.price?.toLocaleString()} × {pb.ruleNights} night{pb.ruleNights !== 1 ? 's' : ''}</span>
                          <span className="text-gray-900">₦{(pb.appliedRule.price * pb.ruleNights).toLocaleString()}</span>
                        </div>
                      </>
                    ) : pb?.appliedRule ? (
                      <>
                        <div className="flex items-center gap-2 px-3 py-2 bg-pink-50 rounded-lg border border-pink-100">
                          <span className="text-xs font-semibold text-pink-700">{pb.appliedRule.label}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">₦{pb.nightlyRate?.toLocaleString()} × {nights} nights</span>
                          <span className="text-gray-900">₦{nightlyTotal.toLocaleString()}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">₦{(pb?.basePrice ?? property.price)?.toLocaleString()} × {nights} nights</span>
                        <span className="text-gray-900">₦{nightlyTotal.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Refundable security deposit</span>
                      <span className="text-gray-900">₦{securityDeposit.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">VAT (7.5%)</span>
                      <span className="text-gray-900">₦{taxes.toLocaleString()}</span>
                    </div>
                    <hr className="border-gray-200" />
                    <div className="flex justify-between font-semibold">
                      <span className="text-gray-900">Total</span>
                      <span className="text-gray-900">₦{total.toLocaleString()}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Contact Host */}
              <button
                onClick={handleContactHost}
                className="w-full border border-gray-300 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <Phone size={16} />
                <span>Contact Host</span>
              </button>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Where you'll be</h2>
          <div className="mb-4">
            <PropertyMap
              location={property.location}
              title={property.title}
            />
          </div>
          {/* <p className="text-gray-700">Located in the heart of Malibu with easy access to beaches, restaurants, and hiking trails. The property offers privacy while being just minutes from local attractions.</p> */}
        </div>
      </div>

      {/* Photo Gallery Modal */}
      {showGallery && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="relative max-w-6xl max-h-screen">
            <button
              onClick={closeGallery}
              className="absolute top-4 right-4 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 text-white transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="bg-white rounded-lg overflow-hidden">
              <img
                src={property.images[currentImageIndex]}
                alt={`Property photo ${currentImageIndex + 1}`}
                className="w-full h-full max-h-screen object-contain"
              />
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => navigateGallery('prev')}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-3 text-white transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7 7M8 14V6m0 8l8 8m0-8l-8 8" />
              </svg>
            </button>

            <button
              onClick={() => navigateGallery('next')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-3 text-white transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7m0 0l-7-7-7" />
              </svg>
            </button>

            {/* Photo Counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 px-3 py-2 rounded-full text-white text-sm">
              {currentImageIndex + 1} / {property.images.length}
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={closeLoginModal}
        onSuccess={handleLoginSuccess}
        onSwitchToRegister={openRegisterModal}
      />

      {/* Register Modal */}
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={closeRegisterModal}
        onSuccess={handleRegisterSuccess}
        onSwitchToLogin={handleSwitchToLogin}
      />

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={closePaymentModal}
        onSuccess={handlePaymentSuccess}
        propertyData={property}
        bookingData={bookingData}
      />

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Share this property</h3>
              <button
                onClick={closeShareModal}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Share this amazing property with others:</p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium text-gray-900 mb-1">{property.title}</p>
                <p className="text-sm text-gray-600">{property.location}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={shareViaNative}
                className="flex items-center justify-center space-x-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.032 4.026a9.001 9.001 0 01-7.432 0m9.032-4.026A9.001 9.001 0 0112 3c-4.474 0-8.268 3.12-9.032 7.326m0 0A9.001 9.001 0 0012 21c4.474 0 8.268-3.12 9.032-7.326" />
                </svg>
                <span className="text-sm font-medium text-gray-700">Share</span>
              </button>

              <button
                onClick={() => copyToClipboard(window.location.href)}
                className="flex items-center justify-center space-x-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                <span className="text-sm font-medium text-gray-700">Copy Link</span>
              </button>

              <button
                onClick={() => {
                  const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`;
                  window.open(url, '_blank');
                }}
                className="flex items-center justify-center space-x-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                <span className="text-sm font-medium text-gray-700">Facebook</span>
              </button>

              <button
                onClick={() => {
                  const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out this amazing property: ${property.title} in ${property.location}`)}&url=${encodeURIComponent(window.location.href)}`;
                  window.open(url, '_blank');
                }}
                className="flex items-center justify-center space-x-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5 text-sky-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>
                <span className="text-sm font-medium text-gray-700">Twitter</span>
              </button>
            </div>

            <div className="border-t pt-4">
              <p className="text-xs text-gray-500 text-center">
                Share this property link with friends and family
              </p>
            </div>
          </div>
        </div>
      )}

      {/* All Reviews Modal */}
      {showAllReviewsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">All Reviews</h3>
                <div className="flex items-center mt-1">
                  <Star className="text-yellow-400 mr-1" size={16} />
                  <span className="font-semibold">{property.rating}</span>
                  <span className="text-gray-600 ml-1">• {property.reviews.length} reviews</span>
                </div>
              </div>
              <button
                onClick={closeAllReviewsModal}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                {property.reviews.map((review, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-3 mb-3">
                      <img src={review.image} alt={review.name} className="w-12 h-12 rounded-full" />
                      <div>
                        <p className="font-semibold text-gray-900">{review.name}</p>
                        <p className="text-sm text-gray-600">{review.date}</p>
                      </div>
                    </div>
                    <p className="text-gray-700">"{review.comment}"</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Host Modal */}
      {showContactHostModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">Contact {property.host.name}</h3>
              <button
                onClick={closeContactHostModal}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">R</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{property.host.name}</p>
                  <p className="text-sm text-gray-600">{property.host.status}</p>
                </div>
              </div>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleSendMessage(formData.get('message'));
              }}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your message
                  </label>
                  <textarea
                    name="message"
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Hi ${property.host.name}! I'm interested in your property...`}
                    required
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Send Message
                  </button>
                  <button
                    type="button"
                    onClick={closeContactHostModal}
                    className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetails;
